/**
 * User-input folding for the work log (ticket 3525, upstream pin e3e7cc3f,
 * MIT), adapted from upstream
 * `packages/client-runtime/src/work-log/userInput.ts`.
 *
 * Forge adaptation: the answer payload is validated structurally instead of
 * through an effect Schema, and the question-tool projection is a local
 * minimal copy of upstream's `shared/toolActivity.projectQuestionToolInput`.
 * Behavior is otherwise kept at the pin.
 *
 * @module workLogUserInput
 */

import type { OrchestrationThreadActivity } from "@cmmd-forge/contracts";

/** One attachment captured with a question answer (structural subset). */
export interface UserInputAnswerAttachment {
  readonly id: string;
  readonly name: string;
  readonly type?: string;
}

/** Structural stand-in for upstream's `UserInputAttachmentAnswerPayload`. */
export interface UserInputAnswerPayload {
  readonly requestId: string;
  readonly questionTextById?: Record<string, string> | undefined;
  readonly answers: Readonly<Record<string, unknown>>;
  readonly attachmentsByQuestionId: Readonly<
    Record<string, ReadonlyArray<UserInputAnswerAttachment>>
  >;
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

const EMPTY_RECORD: Record<string, unknown> = {};

function trimmedNonEmptyString(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

/** Structural validation in place of upstream's effect Schema `is` check. */
// fallow-ignore-next-line complexity -- port of upstream work-log/userInput.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function isUserInputAnswerPayload(value: unknown): value is UserInputAnswerPayload {
  const payload = record(value);
  if (!payload || !trimmedNonEmptyString(payload.requestId)) return false;
  if (!record(payload.answers) || !record(payload.attachmentsByQuestionId)) return false;
  const questionTexts = record(payload.questionTextById);
  if (payload.questionTextById !== undefined && !questionTexts) return false;
  if (questionTexts) {
    for (const text of Object.values(questionTexts)) {
      if (typeof text !== "string") return false;
    }
  }
  for (const attachments of Object.values(
    payload.attachmentsByQuestionId as Record<string, ReadonlyArray<UserInputAnswerAttachment>>,
  )) {
    if (!Array.isArray(attachments)) return false;
    for (const attachment of attachments) {
      const entry = record(attachment);
      if (!entry || !trimmedNonEmptyString(entry.id) || !trimmedNonEmptyString(entry.name)) {
        return false;
      }
    }
  }
  return true;
}

function displayOptionAnswer(value: unknown, labels: ReadonlyMap<string, string>): unknown {
  if (typeof value === "string") return labels.get(value) ?? value;
  if (Array.isArray(value)) return value.map((answer) => displayOptionAnswer(answer, labels));
  const nested = record(value);
  return nested && "answers" in nested
    ? { ...nested, answers: displayOptionAnswer(nested.answers, labels) }
    : value;
}

function questionFingerprint(
  turnId: string,
  questions: ReadonlyArray<unknown>,
): string | undefined {
  const texts = questions.map((question) => (typeof question === "string" ? question.trim() : ""));
  // Sort the fresh array in place because Hermes does not provide toSorted.
  return texts.length > 0 && texts.every(Boolean)
    ? // oxlint-disable-next-line unicorn/no-array-sort -- byte-faithful port of upstream work-log/userInput.ts at pin e3e7cc3f, which sorts in place because Hermes lacks toSorted
      JSON.stringify([turnId, texts.sort()])
    : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Extracts a canonical question list from a question tool's payload, so a
 * duplicate native tool row can be recognized against the folded answer.
 * Local minimal copy of upstream `shared/toolActivity.projectQuestionToolInput`.
 */
// fallow-ignore-next-line complexity -- port of upstream work-log/userInput.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function projectQuestionToolInput(
  data: Record<string, unknown>,
  title: unknown,
): { input?: { questions: ReadonlyArray<{ question: string | null }> } } {
  const item = asRecord(data.item);
  const toolName = data.toolName ?? data.tool ?? item?.tool ?? title;
  if (typeof toolName !== "string") return {};
  const name = toolName
    .split(/__|[./]/)
    .at(-1)
    ?.replace(/[_\s]/g, "")
    .toLowerCase();
  if (!name || !/^(askuserquestion|requestuserinput(?:async)?|askquestion|question)$/.test(name)) {
    return {};
  }
  const input = asRecord(
    data.input ?? data.rawInput ?? asRecord(data.state)?.input ?? item?.arguments,
  );
  const questions = input?.questions ?? asRecord(input?.params)?.questions;
  if (!Array.isArray(questions)) return {};
  return {
    input: {
      questions: questions.map((value) => {
        const question = asRecord(value);
        return {
          question: asTrimmedString(
            question?.question ?? question?.question_text ?? question?.prompt ?? question?.title,
          ),
        };
      }),
    },
  };
}

// fallow-ignore-next-line complexity -- port of upstream work-log/userInput.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function withoutDuplicateQuestionTools(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): ReadonlyArray<OrchestrationThreadActivity> {
  const questions = new Set<string>();
  for (const activity of activities) {
    if (activity.kind !== "user-input.answer-submitted" || !activity.turnId) continue;
    const payload = record(activity.payload);
    const texts = Object.values(record(payload?.questionTextById) ?? EMPTY_RECORD);
    const fingerprint = questionFingerprint(activity.turnId, texts);
    if (fingerprint) questions.add(fingerprint);
  }
  if (questions.size === 0) return activities;
  const duplicateToolIds = new Set<string>();
  for (const activity of activities) {
    if (!activity.kind.startsWith("tool.") || !activity.turnId) continue;
    const payload = record(activity.payload);
    if (typeof payload?.toolCallId !== "string") continue;
    const input = projectQuestionToolInput(record(payload.data) ?? {}, payload.title).input;
    if (!input) continue;
    const fingerprint = questionFingerprint(
      activity.turnId,
      input.questions.map((question) => question.question),
    );
    if (fingerprint && questions.has(fingerprint)) {
      duplicateToolIds.add(JSON.stringify([activity.turnId, payload.toolCallId]));
    }
  }
  return activities.filter((activity) => {
    const payload = record(activity.payload);
    const toolCallId = payload?.toolCallId;
    return (
      activity.tone === "error" ||
      /^(failed|declined|stopped|cancelled)$/.test(String(payload?.status)) ||
      !activity.kind.startsWith("tool.") ||
      typeof toolCallId !== "string" ||
      !duplicateToolIds.has(JSON.stringify([activity.turnId, toolCallId]))
    );
  });
}

/** Keep a question and its answer at the original tool position in the work log. */
// fallow-ignore-next-line complexity -- port of upstream work-log/userInput.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function foldUserInputActivities(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): ReadonlyArray<OrchestrationThreadActivity> {
  const requests = new Map<string, OrchestrationThreadActivity[]>();
  for (const activity of activities) {
    if (
      activity.kind !== "user-input.requested" &&
      activity.kind !== "user-input.resolved" &&
      activity.kind !== "user-input.answer-submitted"
    )
      continue;
    const requestId = record(activity.payload)?.requestId;
    if (typeof requestId !== "string" || !requestId) continue;
    const group = requests.get(requestId) ?? [];
    group.push(activity);
    requests.set(requestId, group);
  }
  const replacements = new Map<OrchestrationThreadActivity, OrchestrationThreadActivity | null>();
  for (const [requestId, group] of requests) {
    const payloads = group.map((activity) => record(activity.payload)!);
    const questions = new Map<string, Record<string, unknown>>();
    const texts = new Map<string, unknown>();
    for (const payload of payloads) {
      for (const [id, text] of Object.entries(record(payload.questionTextById) ?? EMPTY_RECORD))
        texts.set(id, text);
      for (const value of Array.isArray(payload.questions) ? payload.questions : []) {
        const question = record(value);
        if (typeof question?.id !== "string") continue;
        questions.set(question.id, question);
        if (typeof question.question === "string") texts.set(question.id, question.question);
      }
    }
    const questionTextById = Object.fromEntries(texts);
    const submitted = group.findLast(
      (activity) =>
        activity.kind === "user-input.answer-submitted" &&
        record(record(activity.payload)?.answers),
    );
    const rawAnswers =
      record(record(submitted?.payload)?.answers) ??
      payloads.map((payload) => record(payload.answers)).findLast(Boolean) ??
      {};
    const answers = Object.fromEntries(
      Object.entries(rawAnswers).map(([id, value]) => {
        const options = questions.get(id)?.options;
        const labels = new Map<string, string>();
        for (const candidate of Array.isArray(options) ? options : []) {
          const option = record(candidate);
          // Forge question options carry `label` (and no separate wire value),
          // so an option answers as its own label.
          if (typeof option?.label === "string") {
            if (typeof option.value === "string") labels.set(option.value, option.label);
            labels.set(option.label, option.label);
          }
        }
        return [id, displayOptionAnswer(value, labels)];
      }),
    );
    const attachmentsByQuestionId = Object.fromEntries(
      payloads.flatMap((payload) =>
        Object.entries(record(payload.attachmentsByQuestionId) ?? EMPTY_RECORD),
      ),
    );
    const answer = { requestId, questionTextById, answers, attachmentsByQuestionId };
    if (!isUserInputAnswerPayload(answer)) continue;
    const submittedAnswer =
      Object.keys(answers).length > 0 || Object.keys(attachmentsByQuestionId).length > 0;
    for (const activity of group) replacements.set(activity, null);
    replacements.set(group[0]!, {
      ...group[0]!,
      kind: "user-input.answer-submitted",
      tone: "tool",
      summary: submittedAnswer
        ? "User input submitted"
        : group.some((activity) => activity.kind === "user-input.resolved")
          ? "User input dismissed"
          : "User input requested",
      payload: answer,
    });
  }
  return withoutDuplicateQuestionTools(
    activities.flatMap((activity) => {
      const replacement = replacements.get(activity);
      return replacement === null ? [] : [replacement ?? activity];
    }),
  );
}

function getQuestionAnswerText(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(getQuestionAnswerText).filter(Boolean).join(", ");
  const nested = record(value);
  return nested ? getQuestionAnswerText(nested.answers) : "";
}

export function getQuestionAnswerPreview(answer: UserInputAnswerPayload): string {
  const answers = Object.values(answer.answers).map(getQuestionAnswerText).filter(Boolean);
  const attachments = Object.values(answer.attachmentsByQuestionId)
    .flat()
    .map((attachment) => attachment.name);
  return (
    answers.length > 0
      ? answers.join(" · ")
      : attachments.length > 0
        ? attachments.join(", ")
        : Object.values(answer.questionTextById ?? {}).join(" · ")
  )
    .replace(/\s+/g, " ")
    .trim();
}

export function hasQuestionAnswer(answer: UserInputAnswerPayload): boolean {
  return (
    Object.values(answer.answers).some(getQuestionAnswerText) ||
    Object.values(answer.attachmentsByQuestionId).some((attachments) => attachments.length > 0)
  );
}
